import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from 'react-simple-maps';
import { Plus, Minus } from 'lucide-react';
import type { PassportLocation, PassportStation } from '../../api/passport';
import { resolve } from './geo';
import PassportAiNote from './PassportAiNote';

const BASE_SCALE = 165;
const MIN_ZOOM = 1;
const MAX_ZOOM = 32;

interface MapViewProps {
  locations: PassportLocation[];
  stations?: PassportStation[];
  className?: string;
}

// Self-hosted world topojson (no third-party request → keeps the "no external
// tracking / EU-only" privacy promise intact; also avoids a CDN dependency).
const GEO_URL = '/geo/countries-110m.json';

const KIND_COLOR: Record<string, string> = {
  study: '#ec4899', // pink-500
  work: '#e11d48', // rose-600
};

interface PlottedLoc extends PassportLocation {
  lng: number;
  lat: number;
}

const MapView: React.FC<MapViewProps> = ({ locations, stations = [], className = '' }) => {
  // Markers come from the locations array AND the stations (the LLM doesn't
  // reliably dedupe every station's place into `locations`). Dedupe by coords.
  const plotted: PlottedLoc[] = useMemo(() => {
    const out: PlottedLoc[] = [];
    const seen = new Set<string>();
    const add = (
      city: string | null,
      country: string | null,
      kind: PassportLocation['kind'],
      home: boolean,
      lat?: number | null,
      lng?: number | null,
    ) => {
      const p = resolve(city, country, lat, lng);
      if (!p) return;
      const key = `${p.lngLat[0]},${p.lngLat[1]}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ city, country, kind, home, lng: p.lngLat[0], lat: p.lngLat[1] });
    };
    for (const loc of locations) add(loc.city, loc.country, loc.kind, loc.home, loc.lat, loc.lng);
    for (const s of stations)
      add(s.city, s.country, s.type === 'education' ? 'study' : 'work', false, s.lat, s.lng);
    return out;
  }, [locations, stations]);

  // Auto-fit: initial center + zoom from the plotted points' bounding box, so a
  // candidate who only worked in DE starts zoomed into Europe. The user can then
  // pinch / wheel / pan / use the buttons to explore further.
  const fit = useMemo(() => {
    if (plotted.length === 0) return { center: [10, 30] as [number, number], zoom: 1 };
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const p of plotted) {
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
    }
    const center: [number, number] = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
    const padding = 1.3;
    const lngSpan = Math.max(maxLng - minLng, 8) * padding;
    const latSpan = Math.max(maxLat - minLat, 8) * padding;
    const zoom = Math.max(1, Math.min(5, Math.min(360 / lngSpan, 180 / latSpan)));
    return { center, zoom };
  }, [plotted]);

  // Controlled map position (auto-fit on data change, then user-driven).
  const [position, setPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: fit.center,
    zoom: fit.zoom,
  });
  useEffect(() => {
    setPosition({ coordinates: fit.center, zoom: fit.zoom });
  }, [fit]);

  const z = position.zoom;
  const zoomBy = (factor: number) =>
    setPosition((p) => ({ ...p, zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, p.zoom * factor)) }));

  // The map SVG has a fixed ~800-wide viewBox that is scaled down to the
  // container width. On phones that factor is ~0.42, so SVG font units render
  // tiny; bump marker/label sizes on small viewports. Everything is divided by
  // the current zoom (z) so it stays visually constant while zooming.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Framer Motion's page-swipe drag (drag="x" on an ancestor motion.div) listens
  // for *pointer* events natively, which fire before React's delegated handler —
  // so a React onPointerDown stopPropagation is too late and the swipe steals the
  // map pan. Stop pointerdown on the wrapper natively (bubble fires before the
  // ancestor) to kill the swipe. d3-zoom uses mouse/touch/wheel, so pan + pinch +
  // wheel keep working untouched.
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const stop = (e: Event) => e.stopPropagation();
    node.addEventListener('pointerdown', stop);
    return () => node.removeEventListener('pointerdown', stop);
  }, []);

  const labelFont = (isMobile ? 24 : 12) / z;
  const labelHalo = (isMobile ? 5 : 3) / z;
  const labelX = (isMobile ? 14 : 9) / z;
  const labelY = (isMobile ? 8 : 4.5) / z;
  const rHome = (isMobile ? 7 : 5) / z;
  const rOther = (isMobile ? 8 : 6) / z;
  const markerStroke = (isMobile ? 2 : 1.4) / z;

  // Journey line: chronological station coords, deduped consecutive points.
  const journey: [number, number][] = useMemo(() => {
    const pts: [number, number][] = [];
    for (const s of stations) {
      const p = resolve(s.city, s.country, s.lat, s.lng);
      if (!p) continue;
      const last = pts[pts.length - 1];
      if (last && last[0] === p.lngLat[0] && last[1] === p.lngLat[1]) continue;
      pts.push(p.lngLat);
    }
    return pts;
  }, [stations]);

  if (plotted.length === 0 && journey.length === 0) {
    return (
      <div className={`flex items-center justify-center py-16 text-slate-400 ${className}`}>
        <div className="text-center">
          <div className="text-2xl mb-1.5">🌍</div>
          <p className="text-xs">No mapped locations yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative max-w-3xl mx-auto px-2 sm:px-4 py-4 ${className}`}>
      <PassportAiNote />
      {/* touch-action:none + stopPropagation keep map gestures (pinch/pan/wheel)
          from fighting the passport's horizontal swipe and vertical page scroll. */}
      <div
        ref={wrapperRef}
        className="relative rounded-2xl border border-slate-200 bg-gradient-to-b from-sky-50 to-white overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
        style={{ touchAction: 'none' }}
      >
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: BASE_SCALE }}
          style={{ width: '100%', height: 'auto' }}
        >
          <ZoomableGroup
            center={position.coordinates}
            zoom={position.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={(pos) => setPosition(pos)}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#eef2f6"
                    stroke="#e2e8f0"
                    strokeWidth={0.4 / z}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: '#e9eef4' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>

            {journey.length >= 2 && (
              <Line
                coordinates={journey}
                stroke="#db2777"
                strokeWidth={1.4 / z}
                strokeLinecap="round"
                strokeDasharray={`${4 / z} ${4 / z}`}
                fill="none"
              />
            )}

            {plotted.map((loc, i) => {
              const label = loc.city || loc.country || '';
              return (
                <Marker key={`${i}-${loc.city ?? loc.country}`} coordinates={[loc.lng, loc.lat]}>
                  <circle
                    r={loc.home ? rHome : rOther}
                    fill={KIND_COLOR[loc.kind] || '#db2777'}
                    fillOpacity={loc.home ? 0.55 : 0.9}
                    stroke="#fff"
                    strokeWidth={markerStroke}
                  />
                  {label && (
                    <text
                      x={labelX}
                      y={labelY}
                      textAnchor="start"
                      fontSize={labelFont}
                      fontWeight={600}
                      fill="#475569"
                      style={{ paintOrder: 'stroke', stroke: '#ffffff', strokeWidth: labelHalo, strokeLinejoin: 'round' }}
                    >
                      {label}
                    </text>
                  )}
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Zoom controls */}
        <div className="absolute bottom-2 right-2 flex flex-col gap-1">
          <button
            type="button"
            onClick={() => zoomBy(1.6)}
            aria-label="Zoom in"
            className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-white/90 border border-slate-200 shadow-sm text-slate-600 hover:text-pink-500 active:scale-95 transition"
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <button
            type="button"
            onClick={() => zoomBy(1 / 1.6)}
            aria-label="Zoom out"
            className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-white/90 border border-slate-200 shadow-sm text-slate-600 hover:text-pink-500 active:scale-95 transition"
          >
            <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: KIND_COLOR.study }} /> Study
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: KIND_COLOR.work }} /> Work
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0 border-t-2 border-dashed border-pink-500" /> Journey
        </span>
      </div>
    </div>
  );
};

export default MapView;
